import { useState } from "react";
import { useAuth } from "@/data/auth";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { LogoMark, Wordmark } from "@/ui/Logo";
import s from "./AuthGate.module.css";

/**
 * The cloud-only gate for plain browser tabs: no anonymous local workspace
 * here, sign in first. Installed PWA users never see this (they keep the
 * offline-capable local mode and the gentle nag instead).
 */
export function AuthGate() {
  const { providers, requestMagicLink } = useAuth();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "queued">("idle");
  const [error, setError] = useState("");

  const submit = async () => {
    if (!email.trim() || state === "sending") return;
    setState("sending");
    setError("");
    const res = await requestMagicLink(email.trim());
    if (res.ok) setState(res.queued ? "queued" : "sent");
    else {
      setState("idle");
      setError(res.error ?? "something went wrong");
    }
  };

  return (
    <div className={s.gate}>
      <div className={s.card}>
        <div className={s.brand}>
          <LogoMark size={28} />
          <Wordmark size={17} />
        </div>
        <h1 className={s.title}>Sign in to start writing</h1>
        <p className={s.subtitle}>
          In the browser, Hydra Notes keeps your notes in your account so they follow you to every
          device.
        </p>

        {state === "sent" || state === "queued" ? (
          <div className={s.sent}>
            Check <strong>{email}</strong> for your sign-in link. It expires in 15 minutes.
            {state === "queued" ? " Delivery may take a little longer than usual right now." : ""}
          </div>
        ) : (
          <>
            {providers.magicLink ? (
              <form
                className={s.form}
                onSubmit={(e) => {
                  e.preventDefault();
                  void submit();
                }}
              >
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  autoFocus
                  onChange={(e) => setEmail(e.target.value)}
                />
                {error ? <span className={s.error}>{error}</span> : null}
                <Button variant="solid" type="submit" disabled={state === "sending"}>
                  {state === "sending" ? "Sending…" : "Email me a sign-in link"}
                </Button>
              </form>
            ) : null}
            {providers.magicLink && providers.hepgg ? <div className={s.divider}>or</div> : null}
            {providers.hepgg ? (
              <a className={s.hepgg} href="/api/auth/hepgg">
                Continue with Hep.gg
              </a>
            ) : null}
          </>
        )}

        <p className={s.foot}>
          Want to write offline without an account? <a href="/welcome">Install the app</a> and notes
          live on your device until you sign in.
        </p>
      </div>
    </div>
  );
}

export function Splash() {
  return (
    <div className={s.splash}>
      <LogoMark size={40} />
    </div>
  );
}
